import { useMutation, useQuery } from '@tanstack/react-query';

import { readShigoAppConfig } from '../../lib/config';
import { useShigoSession } from '../../lib/useShigoSession';
import { createChangelogClient } from './api';
import type { ChangelogFilter } from './model';

export const changelogQueryKey = ['changelog'] as const;

function useChangelogClient() {
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  if (!session) throw new Error('Shigo session is required.');
  return createChangelogClient({
    config: { apiBaseUrl: config.apiBaseUrl, networkId: config.networkId },
    accessToken: session.accessToken,
  });
}

export function useChangelogQuery(filter: ChangelogFilter) {
  const client = useChangelogClient();
  return useQuery({
    queryKey: [...changelogQueryKey, 'list', filter],
    queryFn: () => client.list(filter),
  });
}

export function useChangelogExport() {
  const client = useChangelogClient();
  return useMutation({ mutationFn: (filter: ChangelogFilter) => client.exportList(filter) });
}

export function useChangelogLookups({ enabled }: { enabled: boolean }) {
  const client = useChangelogClient();
  const publishers = useQuery({
    queryKey: [...changelogQueryKey, 'lookups', 'publishers'],
    queryFn: client.listPublishers,
    enabled,
  });
  const placements = useQuery({
    queryKey: [...changelogQueryKey, 'lookups', 'placements'],
    queryFn: client.listPlacements,
    enabled,
  });
  const integrations = useQuery({
    queryKey: [...changelogQueryKey, 'lookups', 'integrations'],
    queryFn: client.listIntegrations,
    enabled,
  });
  const demandPartners = useQuery({
    queryKey: [...changelogQueryKey, 'lookups', 'demand-partners'],
    queryFn: client.listDemandPartners,
    enabled,
  });
  const users = useQuery({
    queryKey: [...changelogQueryKey, 'lookups', 'users'],
    queryFn: client.listUsers,
    enabled,
  });
  const lineItems = useQuery({
    queryKey: [...changelogQueryKey, 'lookups', 'line-items'],
    queryFn: client.listLineItems,
    enabled,
  });
  return { publishers, placements, integrations, demandPartners, users, lineItems };
}
